import React, { useEffect, useMemo } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    Typography,
    IconButton,
} from '@mui/material';
import { Visibility, Download } from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import { useMRTState } from '../../../../hooks/useMRTState';
import { getMRTDefaultProps } from '../../../../helpers/mrt/getMRTDefaultProps';
import { useConsultaDocumentosModule } from '../modules/useConsultaDocumentosModule';

const nomePdf = (nome) => {
    const limpo = String(nome || 'documento').replace(/[\\/:*?"<>|\u0000-\u001f]/g, '_');
    return /\.pdf$/i.test(limpo) ? limpo : `${limpo}.pdf`;
};

export default function DocumentosDetailPanel({ eleicao, permissoes, parametro, isMobile }) {
    const m = useConsultaDocumentosModule({ eleicaoUuid: eleicao.uuid, permissoes });
    const mrt = useMRTState({ storageKey: 'Eleicao.ConsultaDocumentos', pageSizeDefault: Number(parametro?.paginacao) || 5 });
    const defaults = getMRTDefaultProps({ mrt, parametro, isMobile });
    const arquivos = useMemo(
        () =>
            m.lista.flatMap((doc) => {
                const comuns = { documentoUuid: doc.uuid, titulo: doc.titulo, dataDocumento: doc.dataDocumento };
                return [
                    doc.arquivoGerado && {
                        ...comuns,
                        id: `${doc.uuid}:emitido`,
                        tipo: 'emitido',
                        versao: 'PDF emitido',
                        nome: nomePdf(`${doc.titulo || 'documento'}-emitido`),
                    },
                    doc.arquivoAssinado && {
                        ...comuns,
                        id: `${doc.uuid}:assinado`,
                        tipo: 'assinado',
                        versao: 'PDF anexado / assinado',
                        nome: nomePdf(doc.nomeOriginal || `${doc.titulo || 'documento'}-anexo`),
                    },
                ].filter(Boolean);
            }),
        [m.lista]
    );
    const columns = useMemo(
        () => [
            {
                accessorKey: 'titulo',
                header: 'Documento',
                muiTableBodyCellProps: { sx: { whiteSpace: 'normal', overflowWrap: 'anywhere' } },
            },
            { accessorKey: 'versao', header: 'Tipo' },
            {
                accessorKey: 'nome',
                header: 'Arquivo',
                muiTableBodyCellProps: { sx: { whiteSpace: 'normal', overflowWrap: 'anywhere' } },
            },
            {
                accessorKey: 'dataDocumento',
                header: 'Data',
                Cell: ({ cell }) => cell.getValue()?.split('-').reverse().join('/') || '',
            },
        ],
        []
    );
    useEffect(() => {
        const ultima = Math.max(0, Math.ceil(arquivos.length / mrt.pagination.pageSize) - 1);
        if (mrt.pagination.pageIndex > ultima) mrt.setPagination((prev) => ({ ...prev, pageIndex: ultima }));
    }, [arquivos.length, mrt.pagination.pageIndex, mrt.pagination.pageSize, mrt.setPagination]);
    if (!permissoes.autorizado) return null;
    const disabled = m.ocupado || !permissoes.imprimir;
    return (
        <Stack spacing={2} sx={{ minWidth: 0, width: '100%', p: { xs: 0, sm: 1 }, py: 2 }}>
            <Typography variant="subtitle1" sx={{ overflowWrap: 'anywhere' }}>
                Arquivos: {eleicao.descricao}
            </Typography>
            {!permissoes.imprimir && (
                <Alert severity="info">
                    A visualização, impressão e o download exigem a permissão Imprimir da rotina Eleição.
                </Alert>
            )}
            {m.erro && <Alert severity="error">{m.erro}</Alert>}
            {m.carregando && <Typography>Carregando arquivos...</Typography>}
            <Button type="button" sx={{ alignSelf: 'flex-start' }} disabled={m.ocupado} onClick={m.carregar}>
                Atualizar arquivos
            </Button>
            {!m.carregando && !m.erro && !arquivos.length && (
                <Alert severity="info">Esta eleição não possui PDFs emitidos ou anexados.</Alert>
            )}
            <MaterialReactTable
                {...defaults}
                columns={columns}
                data={arquivos}
                getRowId={(row) => row.id}
                manualFiltering={false}
                manualSorting={false}
                manualPagination={false}
                rowCount={undefined}
                enableRowSelection={false}
                enableRowActions
                enableExpanding={false}
                enableExpandAll={false}
                autoResetPageIndex
                enableGlobalFilterModes={false}
                renderRowActions={
                    isMobile
                        ? undefined
                        : ({ row }) => (
                              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                  <IconButton
                                      type="button"
                                      title="Visualizar / imprimir"
                                      aria-label="Visualizar / imprimir"
                                      disabled={disabled}
                                      onClick={() => m.abrir(row.original)}
                                  >
                                      <Visibility />
                                  </IconButton>

                                  <IconButton
                                      type="button"
                                      title="Baixar"
                                      aria-label="Baixar"
                                      disabled={disabled}
                                      onClick={() => m.abrir(row.original, { baixar: true })}
                                  >
                                      <Download />
                                  </IconButton>
                              </Box>
                          )
                }
                renderRowActionMenuItems={
                    isMobile
                        ? ({ row, closeMenu }) => [
                              <MenuItem
                                  key="abrir"
                                  disabled={disabled}
                                  onClick={() => {
                                      closeMenu();
                                      m.abrir(row.original, { novaAba: true });
                                  }}
                              >
                                  Visualizar / imprimir
                              </MenuItem>,
                              <MenuItem
                                  key="baixar"
                                  disabled={disabled}
                                  onClick={() => {
                                      closeMenu();
                                      m.abrir(row.original, { baixar: true });
                                  }}
                              >
                                  Baixar
                              </MenuItem>,
                          ]
                        : undefined
                }
            />
            <Dialog open={Boolean(m.preview)} onClose={m.fecharPreview} fullWidth maxWidth="lg" fullScreen={isMobile}>
                <DialogTitle sx={{ overflowWrap: 'anywhere' }}>{m.preview?.nome || 'Documento'}</DialogTitle>
                <DialogContent sx={{ height: '75vh', p: 1 }}>
                    {m.preview &&
                        (isMobile ? (
                            <Alert severity="info">
                                O PDF abre em outra aba. Se ela não abriu, toque em Abrir PDF para imprimir. Use a opção de
                                impressão do leitor do celular; você também pode baixar o arquivo.
                            </Alert>
                        ) : (
                            <iframe
                                title="Visualização do documento da eleição"
                                src={m.preview.url}
                                style={{ border: 0, width: '100%', height: '100%' }}
                            />
                        ))}
                </DialogContent>
                <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {m.preview && (
                        <>
                            <Button component="a" href={m.preview.url} target="_blank" rel="noopener noreferrer">
                                Abrir PDF para imprimir
                            </Button>
                            <Button component="a" href={m.preview.url} download={m.preview.nome}>
                                Baixar
                            </Button>
                        </>
                    )}
                    <Button type="button" onClick={m.fecharPreview}>
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
